import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MangaCard from '@/components/feature/MangaCard';
import { mangas, categories } from '@/mocks/manga';

export default function Article() {
  const { id } = useParams();
  const manga = mangas.find((m) => m.id === Number(id)) ?? mangas[0];
  const categoryName = categories.find((c) => c.id === manga.categoryId)?.name ?? '漫画';
  const [version, setVersion] = useState(manga.version[0] ?? '简体');

  const related = mangas
    .filter((m) => m.categoryId === manga.categoryId && m.id !== manga.id)
    .slice(0, 5);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: manga.title,
    description: manga.description,
    image: manga.cover,
    author: { '@type': 'Person', name: manga.author },
    publisher: { '@type': 'Organization', name: '日漫屋' },
    dateModified: manga.chapters[0].updated,
    articleSection: categoryName,
    inLanguage: 'zh-CN',
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }} />
      <Navbar />

      <main className="mx-auto max-w-7xl px-4 pt-24 pb-10 md:px-6">
        <Breadcrumb
          items={[
            { label: '首页', to: '/' },
            { label: categoryName, to: `/category/${manga.categoryId}` },
            { label: manga.title },
          ]}
        />

        <section className="grid grid-cols-1 gap-8 lg:grid-cols-3" aria-label="作品信息">
          <div className="lg:col-span-1">
            <div className="overflow-hidden rounded-lg border border-background-200/70 bg-background-100">
              <img
                src={manga.cover}
                alt={`${manga.title}封面`}
                title={`${manga.title}日漫汉化`}
                className="aspect-[3/4] w-full object-cover object-top"
              />
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded bg-secondary-100 px-2.5 py-1 text-xs font-medium text-secondary-900">{categoryName}</span>
              <span
                className={`rounded px-2.5 py-1 text-xs font-medium ${
                  manga.status === '连载中' ? 'bg-accent-100 text-accent-900' : 'bg-background-200 text-foreground-700'
                }`}
              >
                {manga.status}
              </span>
            </div>
            <h1 className="font-heading mt-3 text-3xl font-bold text-foreground-950 md:text-4xl">{manga.title}</h1>
            <p className="mt-2 text-sm text-foreground-600">
              作者：{manga.author} · 更新至 第{manga.latestChapter}话
            </p>

            <div className="mt-4 flex flex-wrap items-center gap-6 text-sm text-foreground-600">
              <span className="flex items-center gap-1">
                <i className="ri-star-fill text-amber-500"></i> {manga.rating.toFixed(1)} 分
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i> {manga.views} 人气
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-bookmark-line"></i> {manga.chapters.length} 话可读
              </span>
            </div>

            {/* Version selector */}
            <div className="mt-6">
              <h2 className="text-sm font-semibold text-foreground-950">选择阅读版本</h2>
              <div className="mt-2 flex flex-wrap gap-2">
                {manga.version.map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setVersion(v)}
                    className={`whitespace-nowrap rounded-full px-5 py-2 text-sm font-medium transition-colors ${
                      version === v ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-700 hover:bg-primary-100'
                    }`}
                  >
                    {v}版
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href={`#chapter-${manga.latestChapter}`}
                className="whitespace-nowrap rounded-full bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600 transition-colors"
              >
                <i className="ri-play-fill"></i> 开始阅读第{manga.latestChapter}话
              </a>
              <Link
                to={`/category/${manga.categoryId}`}
                className="whitespace-nowrap rounded-full border border-background-300/70 bg-background-100 px-6 py-2.5 text-sm font-medium text-foreground-800 hover:border-primary-300 transition-colors"
              >
                更多{categoryName}漫画
              </Link>
            </div>

            <div className="mt-8">
              <h2 className="text-lg font-bold text-foreground-950">作品简介</h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-700">{manga.description}</p>
            </div>
          </div>
        </section>

        {/* Chapters */}
        <section className="mt-12" aria-labelledby="chapter-title">
          <div className="mb-4 flex items-center justify-between">
            <h2 id="chapter-title" className="flex items-center gap-2 text-xl font-bold text-foreground-950">
              <i className="ri-list-ordered-2 text-primary-500"></i> 章节列表（{version}版）
            </h2>
            <span className="text-sm text-foreground-500">共 {manga.chapters.length} 话</span>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {manga.chapters.map((ch) => (
              <a
                key={ch.num}
                href={`#chapter-${ch.num}`}
                className="flex items-center justify-between rounded-lg border border-background-200/70 bg-background-50 px-4 py-3 transition-colors hover:border-primary-300 hover:bg-primary-50"
              >
                <span className="flex items-center gap-2 text-sm text-foreground-800">
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-100 text-xs font-semibold text-primary-700">
                    {ch.num}
                  </span>
                  第{ch.num}话 · {ch.title}
                </span>
                <span className="text-xs text-foreground-500 whitespace-nowrap">{ch.updated}</span>
              </a>
            ))}
          </div>
        </section>

        {/* Related */}
        {related.length > 0 && (
          <section className="mt-12" aria-labelledby="related-title">
            <div className="mb-4 flex items-center justify-between">
              <h2 id="related-title" className="flex items-center gap-2 text-xl font-bold text-foreground-950">
                <i className="ri-thumb-up-line text-accent-500"></i> 同类型推荐
              </h2>
              <Link to={`/category/${manga.categoryId}`} className="text-sm text-primary-700 hover:text-primary-800 whitespace-nowrap">
                更多{categoryName} <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
              {related.map((m) => (
                <MangaCard key={m.id} manga={m} />
              ))}
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}